import React, { useContext } from 'react';
import { useParams } from 'react-router';

import styled from '@emotion/styled';

import { DriverDetailsDataType } from '../../../../hooks/dashboard/driver-list/useDriverDetails';
import useCreateDriverPerformance from '../../../../hooks/dashboard/driver-performance.ts/useCreateDriverPerformance copy';
import {
  UpdateDriverPerformanceDataType,
  UpdateDriverPerformanceInitialValues,
  UpdateDriverPerformanceSchema,
} from '../../../../models/dashboard/driver-list/update-driver-performance';
import { useAppSelector } from '../../../../redux/store';
import { normalizeImageSrc } from '../../../../utils/utils';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import CustomSelectField from '../../../FormCom/CustomSelectField';
import { CustomStars, CustomStarsRadioField } from '../../../FormCom/CustomStarsRadioField';
import { FormComponent } from '../../../FormCom/FormComponent';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';

interface UpdateDriverPerformanceModalProps {
  data?: Partial<DriverDetailsDataType> | null;
  setRefreshData?: React.Dispatch<React.SetStateAction<number>>;
}

const reportTypeOptions: Record<string, string> = {
  'Theft Report': 'Theft',
  'Accident Report': 'Accident',
};

const UpdateDriverPerformanceModal: React.FC<
  UpdateDriverPerformanceModalProps
> = ({ data, setRefreshData }) => {
  const {
    driverId,
    name: driverName,
    profilePicture: profilePictureParam,
  } = useParams();
  const modalContext = useContext(ModalContext);
  const { createDriverPerformance, loading } = useCreateDriverPerformance();
  const { authData } = useAppSelector((state) => state.auth);

  const driverAddress = data?.address?.trim() || '';
  const driverProfilePicture =
    normalizeImageSrc(data?.profilePicture) ||
    normalizeImageSrc(profilePictureParam) ||
    '/static/img/profilePlaceholderLargePlain.png';

  const onSubmit = (values: UpdateDriverPerformanceDataType, actions: any) => {
    createDriverPerformance(
      {
        ...values,
        driverId: driverId ?? '',
        companyId: authData?.user?.companyId,
        severityRating: values.rating,
        reportType: reportTypeOptions[values.reportType ?? ''] || '',
      },
      () => {
        modalContext?.setShowModal(false);
        setRefreshData && setRefreshData((prev) => prev + 1);
        actions.resetForm();
      },
    );
  };

  return (
    <ModalContainer label='Update Driver Performance' width='867px'>
      <DriverHeader>
        <DriverImg
          src={
            driverProfilePicture ??
            '/static/img/profilePlaceholderLargePlain.png'
          }
        />
        <DriverInfo>
          <DriverName>{driverName}</DriverName>
          <DriverId>{driverAddress}</DriverId>
        </DriverInfo>
      </DriverHeader>
      <FormComponent
        initialValues={UpdateDriverPerformanceInitialValues}
        schema={UpdateDriverPerformanceSchema}
        onSubmit={onSubmit}
      >
        <Row>
          <Column>
            <Label>Severity Rating (1 = Least Severe, 5 = Most Severe)</Label>
            <CustomStarsRadioField name='rating' className='slim-error'>
              <CustomStars name='rating' />
            </CustomStarsRadioField>
          </Column>
          <Column>
            <CustomSelectField
              name='reportType'
              id='reportType'
              label='Report Type'
              placeholder='Select'
              optionData={Object.keys(reportTypeOptions)}
            />

            <ButtonArea>
              <FormSubmitButton
                className='clear fill'
                onClick={() => modalContext?.setShowModal(false)}
                btnType='button'
              >
                Cancel
              </FormSubmitButton>
              <FormSubmitButton loading={loading} className='fill'>
                Add Report
              </FormSubmitButton>
            </ButtonArea>
          </Column>
        </Row>
      </FormComponent>
    </ModalContainer>
  );
};

const DriverHeader = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 24px;
  max-width: 827px;
  border-radius: 5px;
`;

const Row = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 16px;

  & > * {
    width: 100%;
  }
`;

const DriverImg = styled.img`
  width: 67px;
  height: 67px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid transparent;
`;

const DriverInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const DriverName = styled.span`
  font-weight: 600;
  font-size: 18px;
  line-height: 18px;
`;

const DriverId = styled.div`
  color: ${({ theme }) => theme.palette.grayTextColor};
  font-size: 14px;
  line-height: 14px;
  font-weight: 400;
`;

const Label = styled.div`
  margin: 0;
  color: ${({ theme }) => theme.palette.grayTextColor};
  font-size: 14px;
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 16px;
`;

export default UpdateDriverPerformanceModal;
